import React, { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  useContactAnalytics,
  useCampaignQuestionReport,
  useCanvasserStats,
  useVendorSummary,
  useAttentionNeeded,
  hasBothCanvasserTypes,
} from '../../hooks/queries';
import type { QuestionReport } from '../../hooks/queries';
import type { CanvasserAnalyticsFilters } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { SummaryCards } from './SummaryCards';
import { AttentionNeededSection } from './AttentionNeededSection';
import { ActivityChart } from './ActivityChart';
import { CanvasserTable } from './CanvasserTable';
import { VendorSummaryCards } from './VendorSummaryCards';
import { VolunteerLeaderboard } from './VolunteerLeaderboard';
import { CollapsibleSection } from './CollapsibleSection';
import { VendorComparisonTable } from './VendorComparisonTable';
import { PaidVsVolunteerSection } from './PaidVsVolunteerSection';

// ============================================================================
// Survey Results
// ============================================================================

const SUPPORT_COLORS: Record<string, string> = {
  StrongYes: '#16a34a',
  LeaningYes: '#65a30d',
  Undecided: '#9ca3af',
  LeaningNo: '#f97316',
  StrongNo: '#dc2626',
};

interface QuestionReportCardProps {
  question: QuestionReport;
  onAnswerClick: (questionId: string, answer?: string) => void;
}

function QuestionReportCard({ question, onAnswerClick }: QuestionReportCardProps) {
  const isFreeText = question.questionType === 'FreeText';
  // Support Level is a synthetic pseudo-question (QuestionId = Guid.Empty) injected by the
  // reporting endpoint for the aggregate card. It has no CampaignQuestionDto row, so the
  // drilldown endpoint can't resolve it — render its breakdowns as read-only.
  const isDrilldownable = !isFreeText && question.questionType !== 'SupportLevel';
  const maxCount = question.breakdowns.length > 0
    ? Math.max(...question.breakdowns.map((b) => b.count))
    : 1;

  const handleRowKeyDown = (e: React.KeyboardEvent, answer: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onAnswerClick(question.questionId, answer);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{question.questionText}</CardTitle>
          <div className="flex gap-1.5">
            <Badge variant="secondary" className="text-xs">
              {question.questionType}
            </Badge>
            {!question.isActive && (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                Inactive
              </Badge>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {question.totalAnswers} {question.totalAnswers === 1 ? 'answer' : 'answers'}
        </p>
      </CardHeader>
      <CardContent>
        {isFreeText ? (
          <p className="text-sm text-muted-foreground">
            Free text answers cannot be aggregated
          </p>
        ) : (
          <>
            <div className="space-y-2.5">
              {question.breakdowns.map((item, index) => {
                const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                const isSupportLevel = question.questionType === 'SupportLevel';
                const barColor = isSupportLevel
                  ? (SUPPORT_COLORS[item.answer] ?? '#6b7280')
                  : 'hsl(var(--primary))';
                const barOpacity = isSupportLevel ? 1 : Math.max(0.4, 1 - index * 0.15);

                const interactiveProps = isDrilldownable
                  ? {
                      role: 'button' as const,
                      tabIndex: 0,
                      'aria-label': `View voters who answered ${item.answer} to "${question.questionText}"`,
                      onClick: () => onAnswerClick(question.questionId, item.answer),
                      onKeyDown: (e: React.KeyboardEvent) => handleRowKeyDown(e, item.answer),
                      className:
                        'flex cursor-pointer items-center gap-3 rounded-md p-1 -mx-1 transition-colors hover:bg-accent/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    }
                  : {
                      className: 'flex items-center gap-3 rounded-md p-1 -mx-1',
                    };

                return (
                  <div key={item.answer} {...interactiveProps}>
                    <span className="w-28 shrink-0 truncate text-sm">{item.answer}</span>
                    <div className="relative h-6 flex-1 overflow-hidden rounded bg-muted">
                      <div
                        className="absolute inset-y-0 left-0 rounded"
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: barColor,
                          opacity: barOpacity,
                        }}
                      />
                    </div>
                    <span className="w-20 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                      {item.count} ({item.percentage}%)
                    </span>
                  </div>
                );
              })}
            </div>
            {isDrilldownable && (
              <div className="pt-3 flex justify-end">
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => onAnswerClick(question.questionId)}
                  className="h-auto p-0"
                >
                  All answerers →
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Main Dashboard Component
// ============================================================================

interface AnalyticsDashboardProps {
  filters: CanvasserAnalyticsFilters;
}

export function AnalyticsDashboard({ filters }: AnalyticsDashboardProps) {
  const navigate = useNavigate();
  const { data: canvasserStatsResponse, isLoading: statsLoading } = useCanvasserStats(filters);
  const canvasserStats = canvasserStatsResponse?.items;
  const benchmarks = canvasserStatsResponse?.benchmarks;
  const { data: vendorSummary } = useVendorSummary(filters);
  const { data: contactData } = useContactAnalytics();
  const { data: reportData } = useCampaignQuestionReport();
  const { data: attentionData } = useAttentionNeeded(filters);

  const [filterNoShows, setFilterNoShows] = useState(false);

  const attentionCount = attentionData
    ? attentionData.items.filter((item) => !item.isDismissed).length
    : undefined;

  const filteredCanvasserStats = useMemo(() => {
    const stats = canvasserStats ?? [];
    if (!filterNoShows) return stats;
    return stats.filter((c) => c.shiftsNoShow > 0);
  }, [canvasserStats, filterNoShows]);

  const handleReliabilityClick = () => {
    setFilterNoShows((prev) => !prev);
  };

  const handleVendorNavigate = (vendorId: string) => {
    navigate({ to: '/app/analytics/vendors/$vendorId', params: { vendorId } });
  };

  const handleCanvasserClick = (userId: string) => {
    navigate({ to: '/app/analytics/canvassers/$userId', params: { userId } });
  };

  const handleAttentionClick = () => {
    document.getElementById('attention-needed-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleQuestionAnswerClick = (questionId: string, answer?: string) => {
    navigate({
      to: '/app/analytics/questions/$questionId/answers',
      params: { questionId },
      search: answer !== undefined ? { answer } : {},
    });
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards — always visible, not collapsible */}
      <SummaryCards
        canvasserStats={canvasserStats ?? []}
        isLoading={statsLoading}
        onReliabilityClick={handleReliabilityClick}
        attentionCount={attentionCount}
        onAttentionClick={handleAttentionClick}
      />

      {/* Attention Needed Section — NOT collapsible, hides when 0 flags */}
      <AttentionNeededSection filters={filters} />

      {/* Contact Activity */}
      <CollapsibleSection sectionId="activity-chart" title="Contact Activity">
        <ActivityChart contactsOverTime={contactData?.contactsOverTime ?? []} />
      </CollapsibleSection>

      {/* Vendor Summary */}
      <CollapsibleSection sectionId="vendor-summary" title="Vendor Summary">
        <VendorSummaryCards
          vendors={vendorSummary ?? []}
          onNavigate={handleVendorNavigate}
        />
      </CollapsibleSection>

      {/* Vendor Comparison Table — hidden when ≤1 vendor */}
      {(vendorSummary?.length ?? 0) > 1 && (
        <CollapsibleSection sectionId="vendor-comparison" title="Vendor Comparison">
          <VendorComparisonTable
            vendors={vendorSummary ?? []}
            onVendorClick={handleVendorNavigate}
            isLoading={!vendorSummary}
          />
        </CollapsibleSection>
      )}

      {/* Canvasser Drill-Down Table */}
      <CollapsibleSection sectionId="canvasser-table" title="Canvasser List">
        <CanvasserTable
          canvasserStats={filteredCanvasserStats}
          onCanvasserClick={handleCanvasserClick}
          isLoading={statsLoading}
          benchmarks={benchmarks}
        />
      </CollapsibleSection>

      {/* Paid vs Volunteer Comparison — hidden when only one type */}
      {hasBothCanvasserTypes(canvasserStats ?? []) && (
        <CollapsibleSection sectionId="paid-vs-volunteer" title="Paid vs Volunteer">
          <PaidVsVolunteerSection canvasserStats={canvasserStats ?? []} />
        </CollapsibleSection>
      )}

      {/* Volunteer Leaderboard */}
      <CollapsibleSection sectionId="volunteer-leaderboard" title="Volunteer Leaderboard">
        <VolunteerLeaderboard canvasserStats={canvasserStats ?? []} />
      </CollapsibleSection>

      {/* Survey Results */}
      {reportData && reportData.questions.length > 0 && (
        <CollapsibleSection sectionId="survey-results" title="Survey Results">
          <div className="flex items-center justify-end pb-4">
            <span className="text-sm text-muted-foreground">
              {reportData.totalContacts.toLocaleString()} total contacts
            </span>
          </div>
          <div className="space-y-4">
            {reportData.questions.map((q) => (
              <QuestionReportCard
                key={q.questionId}
                question={q}
                onAnswerClick={handleQuestionAnswerClick}
              />
            ))}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}
